import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import type { HubGuidesSectionProps } from "@/types/help-hub/presentation";
import { hubBrandTheme, inkOn } from "../brandInk";
import { HubSection } from "./HubSection";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/** Below this a search box is clutter; above it, its absence is the problem. */
const SEARCH_THRESHOLD = 6;

/**
 * The published guides, as cards rather than the bare list v1 shipped.
 *
 * Search runs over names, summaries *and* each guide's `answers` — the
 * questions the knowledge base already recorded that guide as answering — so
 * typing "refund" finds the returns policy even when the word never appears in
 * its title.
 */
export const HubGuidesSection = ({
  topics,
  guideCount,
  branding,
  urlName,
  onAsk,
}: HubGuidesSectionProps) => {
  const [query, setQuery] = useState("");
  const theme = hubBrandTheme(branding);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return topics.filter(topic => topic.guides.length > 0);
    return topics
      .map((topic) => ({
        ...topic,
        guides: topic.guides.filter((guide) =>
          [guide.name, guide.summary ?? "", ...guide.answers]
            .join(" ")
            .toLowerCase()
            .includes(needle),
        ),
      }))
      .filter((topic) => topic.guides.length > 0);
  }, [topics, query]);

  if (!topics.some(topic => topic.guides.length > 0)) return null;

  const matchCount = filtered.reduce((total, topic) => total + topic.guides.length, 0);

  return (
    <HubSection
      id="guides"
      title="Find the right guide"
      subtitle={`${guideCount} published ${guideCount === 1 ? "guide" : "guides"} — read at your pace, ask whenever you need.`}
      genieIcon="book-open"
      branding={branding}
      action={
        guideCount >= SEARCH_THRESHOLD ? (
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-genie-text-muted" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search guides"
              aria-label="Search guides"
              className="min-h-11 w-full rounded-xl border border-genie-border bg-white py-2 pl-9 pr-3 text-base outline-none focus:ring-2 sm:w-64 sm:text-sm"
              style={{ ["--tw-ring-color" as string]: `${branding.primaryColor}59` }}
            />
          </div>
        ) : undefined
      }
    >
      {matchCount === 0 ? (
        <div className="rounded-2xl border border-dashed border-genie-border bg-white/60 p-6 text-center">
          <p className="text-[14px] text-genie-text-sub">
            No guide matches “{query.trim()}”.
          </p>
          <button
            type="button"
            onClick={() => onAsk(query.trim())}
            className="mt-3 inline-flex min-h-11 items-center rounded-xl px-5 py-2.5 text-[14px] font-semibold"
            style={{ background: theme.gradient, color: inkOn(theme.primary) }}
          >
            Ask the Genie instead
          </button>
        </div>
      ) : (
        <div className="space-y-7">
          {filtered.map((topic) => (
            <div key={topic.id}>
              <div className="mb-3 flex items-baseline gap-2">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 self-center rounded-full"
                  style={{ backgroundColor: branding.primaryColor }}
                />
                <span className="text-[12px] font-semibold uppercase tracking-wide text-genie-text-muted">
                  {topic.name}
                </span>
                <span className="text-[12px] text-genie-text-muted">{topic.guides.length}</span>
              </div>
              <div className="hub-guide-grid">
                {topic.guides.map((guide) => (
                  <Link
                    key={guide.id}
                    to={`/help/${encodeURIComponent(urlName)}/${encodeURIComponent(guide.id)}`}
                    className="hub-published-guide"
                    style={{ borderColor: `${branding.primaryColor}26` }}
                  >
                    <span className="hub-guide-card-top"><GenieIcon name="book-open" size={28} color={theme.primary} secondaryColor={theme.secondary} gradientEnabled={theme.gradientOn} label="" /><span>PUBLISHED GUIDE</span><ArrowUpRight className="h-5 w-5" /></span>
                    <span className="hub-guide-card-title">
                      {guide.name}
                    </span>
                    {guide.summary && (
                      <span className="mt-1.5 line-clamp-3 text-[13px] leading-relaxed text-genie-text-sub">
                        {guide.summary}
                      </span>
                    )}
                    {guide.answers.length > 0 && (
                      <ul className="hub-guide-answers" aria-label="Questions covered">{guide.answers.slice(0, 3).map((answer, index) => <li key={`${index}-${answer}`}><span aria-hidden>{index + 1}</span>{answer}</li>)}</ul>
                    )}
                    <span className="hub-guide-card-footer">Read this guide <ArrowUpRight className="h-4 w-4" /></span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </HubSection>
  );
};

export default HubGuidesSection;
